/**
 * <ShareStorefrontModal />
 *
 * Viral-loop surface for the dealer storefront:
 *   * Generates a QR code (rendered as SVG by qrcode.react) pointing at
 *     the public storefront URL.
 *   * Shows the read-only URL with an inline "Copy Link" button that
 *     flips to "Copied!" for 2 seconds.
 *   * Includes native share-sheet support on mobile (`navigator.share`).
 */
import * as React from "react";
import { QRCodeSVG } from "qrcode.react";
import { Check, Copy, ExternalLink, Share2 } from "lucide-react";
import { Button } from "./Button";
import { Modal } from "./Modal";

interface Props {
  open: boolean;
  onClose: () => void;
  /** Full public URL for the dealer storefront. */
  storefrontUrl: string;
  /** Display name (e.g. "Harbor Supply Co."). */
  dealerName: string;
}

export function ShareStorefrontModal({
  open,
  onClose,
  storefrontUrl,
  dealerName,
}: Props) {
  const [copied, setCopied] = React.useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(storefrontUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback: select text so the user can copy manually.
      const input = document.getElementById("share-url-input") as HTMLInputElement | null;
      input?.select();
    }
  };

  const nativeShare = async () => {
    if (!navigator.share) return;
    try {
      await navigator.share({
        title: `${dealerName} on Parts Buoy`,
        text: `Check out ${dealerName}'s marine parts storefront on Parts Buoy.`,
        url: storefrontUrl,
      });
    } catch {
      /* user cancelled share sheet -- nothing to do */
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Share your storefront"
      description="Post this link or QR code on socials, WhatsApp, or print it on a dock flyer."
      testid="share-storefront-modal"
    >
      {/* QR code panel ------------------------------------------------- */}
      <div className="rounded-2xl bg-surface-sunken border border-surface-border p-5 flex flex-col items-center">
        <div
          data-testid="share-storefront-qr"
          className="rounded-2xl bg-white p-4 shadow-card"
        >
          <QRCodeSVG
            value={storefrontUrl}
            size={200}
            level="M"
            fgColor="#0A0A0B"
            bgColor="#FFFFFF"
          />
        </div>
        <p className="mt-3 text-xs text-ink-muted text-center max-w-xs">
          Scan to jump straight to{" "}
          <span className="font-semibold text-ink">{dealerName}</span>'s storefront.
        </p>
      </div>

      {/* Read-only URL + copy ------------------------------------------- */}
      <div className="mt-4">
        <label
          htmlFor="share-url-input"
          className="mb-1.5 block font-semibold text-ink text-sm"
        >
          Shareable link
        </label>
        <div className="flex items-stretch gap-2">
          <input
            id="share-url-input"
            data-testid="share-storefront-url"
            readOnly
            value={storefrontUrl}
            onFocus={(e) => e.currentTarget.select()}
            className="flex-1 min-w-0 h-12 rounded-xl border-2 border-surface-border bg-white px-3 text-sm font-mono text-ink outline-none focus:border-ink"
          />
          <Button
            type="button"
            variant={copied ? "primary" : "outline"}
            size="md"
            onClick={copy}
            leadingIcon={
              copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />
            }
            data-testid="share-storefront-copy"
          >
            {copied ? "Copied!" : "Copy link"}
          </Button>
        </div>
      </div>

      {/* Footer actions ------------------------------------------------- */}
      <div className="mt-5 flex flex-col sm:flex-row gap-2">
        {typeof navigator !== "undefined" && (navigator as any).share && (
          <Button
            type="button"
            variant="primary"
            size="md"
            fullWidth
            onClick={nativeShare}
            leadingIcon={<Share2 className="h-4 w-4" />}
            data-testid="share-storefront-native"
          >
            Share…
          </Button>
        )}
        <a
          href={storefrontUrl}
          target="_blank"
          rel="noreferrer"
          className="w-full"
        >
          <Button
            type="button"
            variant="outline"
            size="md"
            fullWidth
            leadingIcon={<ExternalLink className="h-4 w-4" />}
            data-testid="share-storefront-open"
          >
            Open storefront
          </Button>
        </a>
      </div>
    </Modal>
  );
}
